import {
    Logger, Matrix, namespace, Point,
} from '../../../../../FrontendBundle/Resources/js/ofcs';
import AbstractItem from './abstractItem';

namespace('Ofcs.Editor.Item');

/**
 * @class GripItem
 * @extends AbstractItem
 */
export default window.Ofcs.Editor.Item.GripItem = Ofcs.Editor.Item.AbstractItem.extend({

    /**
     * Item type
     *
     * @property {string} type
     */
    type: 'grip',

    /**
     * "Safety margin" of grips is fix 2 mm.
     */
    safetyMargin: 2,

    supportsRotation: true,
    supportsWidth: true,
    supportsPositioning: true,
    supportsOutOfFoam: true,
    supportsGuides: true,

    defaults: {
        /**
         * Figure type
         * @property {string} attributes.type
         */
        type: 'Grip',

        /**
         * Current rotation
         * @property {number} attributes.rotation
         */
        rotation: 0,

        /**
         * Current position
         * @property {Point} attributes.position
         */
        position: new Point(0, 0),

        /**
         * Width of the shape
         * @property {number} attributes.width
         */
        width: 0,

        /**
         * Height of the shape
         * @property {number} attrubutes.height
         */
        height: 25,

        /**
         * Radius of the grip shape corners
         * @property {number} attributes.radius
         */
        radius: 12.5,

        /**
         * Depth of the grip bag
         * @property {number} attributes.depth
         */
        depth: 25,
    },

    /**
     * Supported depths for the grip item
     *
     * @property {number[]}
     */
    supportedDepths: [],

    /**
     * Initializes a new grip item
     *
     * @private
     */
    initialize() {
        AbstractItem.prototype.initialize.call(this);

        this.initializeSupportedDepths();

        // Initialize paths
        this.set('ext', this.calculateSegments());
        this.set('orig', this.get('ext'));
        this.set('safety', this.calculateSegments(this.getSafetyMargin()));

        this.on('change', this.onItemChange, this);
    },

    initializeSupportedDepths() {
        const stepSize = 5;
        const supportedDepths = [];

        for (let depth = this.getMaxDepth(); depth >= this.getMinDepth(); depth -= stepSize) {
            supportedDepths.push(depth);
        }

        supportedDepths.reverse();

        this.supportedDepths = supportedDepths;
    },

    /**
     * Return safety margin for this item
     *
     * @return {number}
     */
    getSafetyMargin() {
        return this.safetyMargin;
    },

    /**
     * Callback gets executed if the item receives changes on any attribute
     *
     * @param {Ofcs.Editor.Item.GripItem} item this
     */
    onItemChange(item) {
        const changes = item.changed;

        if (changes.width !== undefined) {
            this.set('ext', this.calculateSegments());
            this.set('orig', this.get('ext'));
            this.set('safety', this.calculateSegments(this.getSafetyMargin()));
            this.trigger('updatesegments', this);
        }

        if (changes.depth !== undefined) {
            this.set('safety', this.calculateSegments(this.getSafetyMargin()));
            this.trigger('updatesegments', this);
        }
    },

    /**
     * Calculates segments for rendering the grip bag
     *
     * @param {number} spread
     * @return {Array} Array with segments
     */
    calculateSegments(spread) {
        const radius = parseInt(this.get('radius'), 10) + (spread || 0);
        const path = [];
        const step = 12;
        const width = this.get('width');
        const halfWidth = (width / 2) - parseInt(this.get('radius'), 10);

        path.push(['M', -halfWidth, -radius]);
        path.push(['L', halfWidth, -radius]);

        for (let angle = -90; angle <= 90; angle += step) {
            const x = radius * Math.cos((angle * Math.PI) / 180);
            const y = radius * Math.sin((angle * Math.PI) / 180);

            path.push(['L', x + halfWidth, y]);
        }

        path.push(['L', -halfWidth, radius]);

        for (let angle = 90; angle <= 270; angle += step) {
            const x = radius * Math.cos((angle * Math.PI) / 180);
            const y = radius * Math.sin((angle * Math.PI) / 180);

            path.push(['L', x - halfWidth, y]);
        }

        return [path];
    },

    /**
     * Get middle-left point respect rotation.
     *
     * @return {Point}
     */
    getPointLeft() {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.get('width');
        const point = new Point(center.x - (width / 2), center.y);

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get middle-right point respect rotation.
     *
     * @return {Point}
     */
    getPointRight() {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.get('width');
        const point = new Point(center.x + (width / 2), center.y);

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    set(key, value, optionsParam) {
        const { attrs, options } = this._normalizeSetParameters(key, value, optionsParam);

        if (attrs.width !== undefined) {
            attrs.width = Math.max(this.getMinWidth(), attrs.width);
            Logger.debug('gripItem.set() | fixed width', attrs.width);
        }
        if (attrs.depth !== undefined) {
            attrs.depth = Math.max(this.getMinDepth(), attrs.depth);
            attrs.depth = Math.min(this.getMaxDepth(), attrs.depth);
            Logger.debug('gripItem.set() | fixed depth', attrs.depth);
        }

        return AbstractItem.prototype.set.call(this, attrs, options);
    },

    /**
     * Translate object into json stringable types. This method will be used
     * by JSON.stringify() automaticly.
     *
     * @return {Object}
     */
    toJSON(...args) {
        const attributes = AbstractItem.prototype.toJSON.apply(this, args);

        return {
            depth: attributes.depth,
            x: attributes.x,
            y: attributes.y,
            rotation: attributes.rotation,
            width: attributes.width,
            height: attributes.height,
            radius: attributes.radius,
            type: attributes.type,
        };
    },

    /**
     * Get minimum width.
     *
     * @return {number}
     */
    getMinWidth() {
        const radius = this.get('radius') || this.defaults.radius;

        return 2 * radius;
    },

    /**
     * Check if width is valid.
     * - greater or equal 2*radius
     *
     * @param {number} width
     * @return {boolean}
     */
    isValidWidth(width) {
        return width >= this.getMinWidth();
    },

    /**
     * Get minimum depth defined in parent item.
     *
     * @return {number}
     */
    getMinDepth() {
        if (!this.hasParentItem()) return 0;

        return this.getParentItem().get('minDepth');
    },

    /**
     * Get maximum depth defined in parent item.
     *
     * @return {number}
     */
    getMaxDepth() {
        if (!this.hasParentItem()) return 9999;

        return this.getParentItem().get('depth');
    },

    /**
     * Initializes item handles
     *
     * @param {Ofcs.Editor.Svg.Canvas} canvas - svg canvas instance
     * @return {Ofcs.Editor.Svg.Handle.ResizeGripHandle}
     */
    initializeHandles(canvas) {
        return new Ofcs.Editor.Svg.Handle.ResizeGripHandle(this, canvas);
    },
});
